import React from "react";
import { Avatar, AvatarFallback } from "components/shadcn/ui/avatar";
import Skeleton from "react-loading-skeleton";
import { ThreadDetailsResponse } from "components/messaging-thread/types";
import { getInitials, getTimeLabelWithDay } from "helpers";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import { BookCheck, PinIcon, Pencil, Save } from "lucide-react";
import { Input } from "components/shadcn/ui/input";

interface Props {
  thread: ThreadDetailsResponse | null;
  loading: boolean;
  handleTogglePin?: (thread: ThreadDetailsResponse) => void;
  handleToggleArchive?: (thread: ThreadDetailsResponse) => void;
  isEditingTitle: boolean;
  setIsEditingTitle: (v: boolean) => void;
  titleDraft: string;
  setTitleDraft: (v: string) => void;
  savingTitle: boolean;
  handleSaveTitle: () => void;
}

const ThreadDetailsHeader: React.FC<Props> = ({
  thread,
  loading,
  handleTogglePin,
  handleToggleArchive,

  isEditingTitle,
  setIsEditingTitle,
  titleDraft,
  setTitleDraft,
  savingTitle,
  handleSaveTitle,
}) => {
  if (loading) {
    return (
      <div className="p-4 flex items-center gap-3 border-b">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-4 w-40" />
      </div>
    );
  }

  if (!thread) return null;

  return (
    <div className="p-4 flex items-center justify-between border-b bg-gray-100 rounded-t-2xl">
      <div className="flex items-center gap-3">
        <Avatar>
          <AvatarFallback>
            {getInitials(
              thread.participant.firstName,
              thread.participant.lastName
            )}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col text-left gap-1">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-md">
              {thread.participant.firstName} {thread.participant.lastName}
            </p>

            {!isEditingTitle ? (
              <>
                <p className="font-semibold text-md text-gray-500">
                  {thread.title}
                </p>
                <ToolTipWrapper content="Edit title">
                  <Pencil
                    size={20}
                    className="cursor-pointer text-gray-500 hover:text-[var(--primary-700)]"
                    onClick={() => setIsEditingTitle(true)}
                  />
                </ToolTipWrapper>
              </>
            ) : (
              <>
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  className="h-8 w-[300px]"
                />
                <ToolTipWrapper content="Save title">
                  <Save
                    size={20}
                    className="cursor-pointer text-[var(--primary-700)]"
                    onClick={handleSaveTitle}
                  />
                </ToolTipWrapper>
              </>
            )}
          </div>

          {thread.lastMessageAt && (
            <p className="text-sm text-primary-500">
              Last message: {getTimeLabelWithDay(thread.lastMessageAt)}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-row gap-5">
        <ToolTipWrapper content={thread.isArchived ? "Unarchive" : "Archive"}>
          <BookCheck
            size={20}
            className={`cursor-pointer transition-colors ${
              thread.isArchived
                ? "text-[var(--primary-700)]"
                : "text-gray-500 hover:text-[var(--primary-700)]"
            }`}
            stroke={thread.isArchived ? "var(--primary-700)" : "currentColor"}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleArchive?.(thread);
            }}
          />
        </ToolTipWrapper>

        <ToolTipWrapper content={thread.isPinned ? "Unpin chat" : "Pin chat"}>
          <PinIcon
            size={20}
            className={`cursor-pointer rotate-45 transition-colors ${
              thread.isPinned
                ? "text-[var(--primary-700)]"
                : "text-gray-500 hover:text-[var(--primary-700)]"
            }`}
            fill={thread.isPinned ? "var(--primary-700)" : "none"}
            stroke={thread.isPinned ? "var(--primary-700)" : "currentColor"}
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePin?.(thread);
            }}
          />
        </ToolTipWrapper>
      </div>
    </div>
  );
};

export default ThreadDetailsHeader;
